/**
 * Media-laag — offline upload-queue.
 *
 * Capture (CameraCapture) enqueue't blobs in Dexie `mediaQueue`; deze worker
 * verwerkt ze zodra er verbinding is: presign → PUT naar R2 → registratie in
 * de `media`-tabel → rij (en dus blob) lokaal verwijderen. Daarmee blijft er
 * niets op het toestel achter — de harde eis van de media-laag.
 *
 * Twee eigenschappen die op 17-08 uit een incident zijn gekomen (een hele
 * zaterdagploeg, 48 kleden, stond dagen op één toestel vast):
 *
 * 1. NIEUWSTE EERST. Oudste-eerst geeft head-of-line blocking: zodra er een
 *    achterstand is begint elke ronde weer bij de foto van gisteren en komt
 *    het kleed waar de wasser nú bij staat nooit aan de beurt. Een achterstand
 *    mag traag weglopen, maar mag nooit vers werk gijzelen.
 * 2. NOOIT DE RIJEN IN ÉÉN ARRAY. Elke rij bevat de blob zelf; een wachtrij
 *    van een paar honderd foto's plus video's (tot 45 MB per stuk) is honderden
 *    MB's en dat sloopt het tabblad op een telefoon. We itereren over de
 *    sleutels en halen elke rij pas op het moment van uploaden op.
 *
 * Retry zonder doodlopend eind: een item gaat na MAX_RETRIES op 'failed' zodat
 * de teller in de header het laat zien, maar blijft aangeboden worden met een
 * oplopende backoff. Een permanente skip zou de enige kopie van een foto
 * onbereikbaar maken tot iemand IndexedDB wist.
 */

import { db, type MediaQueueItem } from '../db/database';
import { useAuthStore } from '../../stores';

/** Vanaf hier heet een item 'failed' in de teller — niet: wordt niet meer geprobeerd. */
const MAX_RETRIES = 5;

/** Backoff per poging: 0s, 30s, 1m, 2m, 4m, daarna elke 5 minuten. */
const MAX_BACKOFF_MS = 5 * 60_000;

function backoffMs(retryCount: number): number {
  if (retryCount <= 0) return 0;
  return Math.min(30_000 * 2 ** (retryCount - 1), MAX_BACKOFF_MS);
}

export interface MediaDoel {
  wiSfId?: string;
  woSfId?: string;
  klachtId?: string;
}

export type MediaFase =
  | 'voorinspectie'
  | 'reinigen'
  | 'na-inspectie'
  | 'klacht'
  | 'risico'
  | 'ophaal'
  | 'aflevering'
  | 'social'
  | 'referentie';

let isProcessing = false;

/**
 * Foto's tegelijk in de lucht. Drie ronde-trips per foto (presign, PUT,
 * register) maken serieel ~2-3 s per stuk; een achterstand van 200 foto's is
 * dan 10 minuten scherm-aan, en dat scherm gaat een keer uit. Drie tegelijk
 * brengt dat naar 3-4 minuten. Niet hoger: elke worker houdt precies één blob
 * in het geheugen (zie kop van dit bestand), en video's tot 45 MB tellen mee.
 */
const UPLOAD_CONCURRENCY = 3;

/**
 * Random id per app-installatie, zodat de serverkant twee toestellen van
 * dezelfde medewerker uit elkaar houdt. localStorage, niet IndexedDB: dit is
 * geen data, alleen een label, en het mag best verdwijnen bij een reset.
 */
function getDeviceId(): string {
  const KEY = 'kleda-field-device-id';
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

/** Recentste uploadfout op dit toestel, voor de telemetrie. In-memory: na een
 *  app-herstart leeg tot de eerstvolgende faalpoging hem opnieuw vult. */
let laatsteFout: string | null = null;

function authHeaders(): Record<string, string> | null {
  const { staff, pin } = useAuthStore.getState();
  if (!staff?.id || !pin) return null;
  return {
    'Content-Type': 'application/json',
    'X-Staff-Id': staff.id,
    'X-Staff-Pin': pin,
  };
}

/**
 * Vraag de browser om de opslag van deze origin als 'persistent' te merken.
 *
 * Zonder dit is de opslag best-effort en mag de browser hem in één keer
 * weggooien onder schijfdruk; iOS Safari evict best-effort origins ook na
 * ongeveer een week zonder gebruik. De wachtrij is de ENIGE kopie van een
 * foto (geen galerij, geen serverkant-spoor tot de upload lukt), dus die
 * vlag is geen luxe.
 *
 * Idempotent en stil: een browser die het niet ondersteunt of de aanvraag
 * weigert mag de app niet ophouden. Retourneert of de opslag nu duurzaam is.
 */
export async function vraagDuurzameOpslag(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    const toegekend = await navigator.storage.persist();
    console.log('[mediaQueue] duurzame opslag:', toegekend ? 'toegekend' : 'geweigerd');
    return toegekend;
  } catch {
    return false;
  }
}

export interface OpslagRuimte {
  gebruiktBytes: number;
  quotaBytes: number;
  /** Aandeel van de quota dat in gebruik is (0-1), of null als onbekend. */
  aandeel: number | null;
}

/** Wat de browser ons over de opslagruimte vertelt. Null als onbekend. */
export async function getOpslagRuimte(): Promise<OpslagRuimte | null> {
  try {
    if (!navigator.storage?.estimate) return null;
    const { usage, quota } = await navigator.storage.estimate();
    if (typeof usage !== 'number' || typeof quota !== 'number' || quota <= 0) return null;
    return { gebruiktBytes: usage, quotaBytes: quota, aandeel: usage / quota };
  } catch {
    return null;
  }
}

/**
 * Zet een capture in de wachtrij en trap direct een verwerkronde af als we
 * online zijn. De aanroeper is klaar zodra de blob in IndexedDB staat.
 *
 * GOOIT als de opslag de blob niet aanneemt (quota vol, IndexedDB dicht). Dat
 * moet ook: de aanroeper is de camera, en een capture die niet is opgeslagen
 * bestaat nergens meer. Stil doorgaan is de foto weggooien.
 */
export async function enqueueMedia(input: {
  blob: Blob;
  mimeType: string;
  type: 'foto' | 'video';
  fase: MediaFase;
  doel: MediaDoel;
  /** Status__c van het WI op het schietmoment (klant-timeline: nabehandeling
   *  vs. eerste reiniging). Optioneel — alleen waar de pagina het item kent. */
  captureStatus?: string;
}): Promise<number> {
  const { staff } = useAuthStore.getState();
  // Bytes inline opslaan (ArrayBuffer), niet als Blob: iOS bewaart een blob
  // als los bestand naast de database en mag dat weggooien terwijl de rij
  // blijft bestaan — dan is de enige kopie stilletjes weg (gemeten 18-08:
  // 75 rijen "Load failed" op één toestel). Inline bytes evicten niet los.
  const id = await db.mediaQueue.add({
    data: await input.blob.arrayBuffer(),
    mimeType: input.mimeType,
    type: input.type,
    fase: input.fase,
    wiSfId: input.doel.wiSfId,
    woSfId: input.doel.woSfId,
    klachtId: input.doel.klachtId,
    captureStatus: input.captureStatus,
    takenAt: new Date(),
    staffId: staff?.id ?? '',
    status: 'pending',
    retryCount: 0,
    createdAt: new Date(),
  });

  if (navigator.onLine) {
    void processMediaQueue();
  }
  return id;
}

export interface MediaQueueStand {
  /** Alles wat nog op het toestel staat: wachtend, bezig en gefaald. */
  aantal: number;
  /** Waarvan MAX_RETRIES keer mislukt — vraagt aandacht, niet: verloren. */
  gefaald: number;
  /** Leeftijd van de oudste capture in ms, of null bij een lege wachtrij. */
  oudsteMs: number | null;
}

/**
 * Stand van de wachtrij voor de teller in de header.
 *
 * Leest bewust alleen de index (`createdAt`) en counts, nooit de rijen: dit
 * draait elke paar seconden en mag de blobs niet aanraken.
 */
export async function getMediaQueueStand(): Promise<MediaQueueStand> {
  const sleutels = await db.mediaQueue.orderBy('createdAt').keys();
  const gefaald = await db.mediaQueue.where('status').equals('failed').count();

  let oudsteMs: number | null = null;
  if (sleutels.length > 0) {
    const eerste = sleutels[0] as unknown;
    const ms = eerste instanceof Date ? eerste.getTime() : new Date(eerste as string).getTime();
    if (!Number.isNaN(ms)) oudsteMs = Date.now() - ms;
  }

  return { aantal: sleutels.length, gefaald, oudsteMs };
}

/**
 * Verwerk de hele wachtrij, item voor item. Veilig om vaak aan te roepen:
 * één vlag voorkomt dubbele verwerkrondes, en elke stap is idempotent
 * (registratie upsert op r2_key aan de serverkant).
 *
 * Video's uploaden we alleen als de verbinding niet als traag bekend staat
 * (Network Information API waar beschikbaar); anders blijven ze in de queue
 * tot een betere verbinding (in de praktijk: wasserij-wifi).
 */
export async function processMediaQueue(): Promise<void> {
  if (isProcessing || !navigator.onLine) return;
  // Niet ingelogd: nu stoppen, vóór de eerste rij. Zonder deze poort loopt de
  // ronde elke 30 seconden de hele wachtrij langs en leest per item de blob in
  // om daarna bij de auth-check alsnog niets te doen — precies de
  // geheugendruk die we hier bestrijden.
  if (!authHeaders()) return;

  isProcessing = true;

  try {
    // Alleen de sleutels: de rijen bevatten de blobs (zie kop van dit bestand).
    const sleutels = (await db.mediaQueue.orderBy('createdAt').primaryKeys()) as number[];
    // Nieuwste eerst, zodat een achterstand vers werk niet gijzelt.
    sleutels.reverse();

    // Vaste pool van workers over één gedeelde cursor: elke worker heeft
    // hooguit één rij (en dus één blob) tegelijk in handen. Video's maximaal
    // één tegelijk — die zijn tot 45 MB en drie ervan naast elkaar is precies
    // de geheugendruk die we hier vermijden.
    let cursor = 0;
    let videoBezig = false;

    const worker = async (): Promise<void> => {
      while (navigator.onLine) {
        const sleutel = sleutels[cursor++];
        if (sleutel === undefined) return;

        const item = await db.mediaQueue.get(sleutel);
        if (!item) continue; // in een eerdere ronde al geland
        if (item.type === 'video' && (isSlowConnection() || videoBezig)) continue;

        const wacht = backoffMs(item.retryCount);
        if (
          wacht > 0 &&
          item.lastAttemptAt &&
          Date.now() - new Date(item.lastAttemptAt).getTime() < wacht
        ) {
          continue;
        }

        if (item.type === 'video') {
          videoBezig = true;
          try {
            await uploadItem(item);
          } finally {
            videoBezig = false;
          }
        } else {
          await uploadItem(item);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, sleutels.length || 1) }, () => worker())
    );
  } finally {
    isProcessing = false;
  }

  // Na elke ronde de stand melden (gethrottled): dit is het enige moment
  // waarop de serverkant kan zien dat een toestel foto's vasthoudt en waarom.
  void meldQueueStand();
}

/** Niet vaker melden dan dit zolang de stand niet verandert. */
const MELD_INTERVAL_MS = 10 * 60_000;

let laatstGemeld = 0;
let laatstGemeldeStand = '';

/**
 * Meld de wachtrijstand aan de server (POST /api/media/queue-stand).
 *
 * De wachtrij is de enige kopie van een foto, en een toestel dat vastzit is
 * vanaf de serverkant onzichtbaar — de key-metrics-alert kon tot nu toe alleen
 * zeggen "zoek het toestel". Deze melding maakt dat: dít toestel, déze fout.
 *
 * Wordt ook los van de uploadronde aangeroepen (sync-engine-tick): als een
 * ronde hangt of vroeg afbreekt moet de stand alsnog binnenkomen — juist dán.
 *
 * Best-effort en stil: telemetrie mag uploads nooit in de weg zitten.
 */
export async function meldQueueStand(): Promise<void> {
  try {
    const headers = authHeaders();
    if (!headers || !navigator.onLine) return;

    const stand = await getMediaQueueStand();
    const signatuur = `${stand.aantal}|${stand.gefaald}|${laatsteFout ?? ''}`;
    const nu = Date.now();
    if (signatuur === laatstGemeldeStand && nu - laatstGemeld < MELD_INTERVAL_MS) return;

    // Opslagruimte mee: op iOS is de quota gekoppeld aan de vrije schijf, dus
    // een krimpende quota = een vollopend toestel. Dat is de diagnose achter
    // "Load failed"-uploads en zelfde-dag-blob-evictie (gemeten 18-08).
    const ruimte = await getOpslagRuimte();

    const res = await fetch('/api/media/queue-stand', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        deviceId: getDeviceId(),
        aantal: stand.aantal,
        gefaald: stand.gefaald,
        oudsteCaptureAt:
          stand.oudsteMs !== null ? new Date(nu - stand.oudsteMs).toISOString() : undefined,
        laatsteFout: laatsteFout ?? undefined,
        opslagGebruiktBytes: ruimte?.gebruiktBytes,
        opslagQuotaBytes: ruimte?.quotaBytes,
      }),
    });
    if (res.ok) {
      laatstGemeld = nu;
      laatstGemeldeStand = signatuur;
    }
  } catch {
    // stil: volgende ronde opnieuw
  }
}

function isSlowConnection(): boolean {
  const conn = (navigator as Navigator & {
    connection?: { effectiveType?: string; saveData?: boolean };
  }).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  return conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
}

/**
 * Timeout per fetch. Zonder timeout bevriest één hangende request (iOS laat
 * een fetch stil openstaan op een wegvallende verbinding) de worker, en omdat
 * `isProcessing` dan nooit vrijkomt ook élke volgende ronde — het toestel
 * lijkt te uploaden maar doet niets meer tot een app-herstart. Dat is het
 * "burstjes met minuten stilte"-patroon uit de meting van 17-08.
 */
function metTimeout(ms: number): AbortSignal | undefined {
  try {
    return AbortSignal.timeout(ms);
  } catch {
    return undefined; // oude browser: liever geen timeout dan geen upload
  }
}

const API_TIMEOUT_MS = 30_000;
/** PUT naar R2 mag lang duren (video tot 45 MB op mobiel netwerk), maar niet eeuwig. */
const PUT_TIMEOUT_FOTO_MS = 2 * 60_000;
const PUT_TIMEOUT_VIDEO_MS = 10 * 60_000;

/**
 * Einde voor een capture waarvan de data onherstelbaar weg is: registreer het
 * verlies bij de server (welk kleed, welke fase, wanneer geschoten — zodat
 * kantoor weet waar het foto-gat zit) en verwijder dán pas de dode rij.
 * Registratie mislukt (offline, storing)? Dan blijft de rij staan en probeert
 * de volgende ronde het opnieuw — het verlies mag nooit stil zijn.
 */
async function verwerkVerlorenCapture(item: MediaQueueItem, reden: string): Promise<void> {
  laatsteFout = reden;
  const headers = authHeaders();
  if (!headers) return;
  try {
    const res = await fetch('/api/media/verloren', {
      method: 'POST',
      headers,
      signal: metTimeout(API_TIMEOUT_MS),
      body: JSON.stringify({
        deviceId: getDeviceId(),
        items: [
          {
            wiSfId: item.wiSfId,
            woSfId: item.woSfId,
            klachtId: item.klachtId,
            fase: item.fase,
            captureStatus: item.captureStatus,
            mimeType: item.mimeType,
            takenAt: item.takenAt ? new Date(item.takenAt).toISOString() : undefined,
            reden,
          },
        ],
      }),
    });
    if (!res.ok) throw new Error(`verloren-registratie ${res.status}`);
    const json = (await res.json()) as { success: boolean };
    if (!json.success) throw new Error('verloren-registratie geweigerd');
    // Pas na geslaagde registratie mag de rij weg (delete werkt wél op een
    // dode rij; update niet).
    await db.mediaQueue.delete(item.id!);
    console.warn('[mediaQueue] verloren capture geregistreerd en opgeruimd:', reden);
  } catch (err) {
    console.warn('[mediaQueue] verloren-registratie faalde, rij blijft staan:', err);
  }
}

async function uploadItem(item: MediaQueueItem): Promise<void> {
  const headers = authHeaders();
  if (!headers) return; // niet ingelogd — laat staan tot de volgende sessie

  const doel: MediaDoel = {
    wiSfId: item.wiSfId,
    woSfId: item.woSfId,
    klachtId: item.klachtId,
  };

  // Capture-bytes ophalen — VÓÓR de eerste db-update. Nieuwe rijen dragen ze
  // inline (`data`); oude rijen dragen een Blob, en iOS Safari bewaart die als
  // verwijzing naar een los bestand dat onder schijfdruk kan sneuvelen terwijl
  // de rij blijft bestaan. Zo'n dode rij is bovendien ONMUTEERBAAR (elke
  // Dexie-update herserialiseert de blob en faalt met "Error preparing
  // Blob/File data", gemeten 18-08) — dus de check moet hier, niet in de
  // upload-try, en het enige einde voor een dode rij is registreren en
  // verwijderen.
  let body: Blob;
  if (item.data) {
    body = new Blob([item.data], { type: item.mimeType });
  } else if (item.blob) {
    // VOLLEDIGE leestest, niet alleen de eerste bytes: iOS houdt een inline-
    // prefix in de database terwijl het onderliggende blob-bestand weg kan
    // zijn — een 64-byte-check slaagt dan, waarna de PUT alsnog breekt met
    // "Load failed" en het item eeuwig blijft spinnen (gemeten 18-08: 11 MB
    // opslag bij 200 wachtende rijen). Wat de leestest overleeft gaat als
    // gelezen bytes omhoog, zodat de fetch de blob niet nóg een keer hoeft
    // te streamen.
    let bytes: ArrayBuffer | null = null;
    try {
      bytes = await item.blob.arrayBuffer();
    } catch {
      bytes = null;
    }
    if (bytes === null || bytes.byteLength === 0) {
      await verwerkVerlorenCapture(item, 'blob-onleesbaar: iOS heeft de foto-data weggegooid');
      return;
    }
    body = new Blob([bytes], { type: item.mimeType });
  } else {
    await verwerkVerlorenCapture(item, 'rij zonder data of blob');
    return;
  }

  try {
    await db.mediaQueue.update(item.id!, {
      status: 'uploading',
      lastAttemptAt: new Date(),
    });

    // 1. Presign
    const presignRes = await fetch('/api/media/presign', {
      method: 'POST',
      headers,
      signal: metTimeout(API_TIMEOUT_MS),
      body: JSON.stringify({
        doel,
        fase: item.fase,
        mimeType: item.mimeType,
        bytes: body.size,
      }),
    });
    if (!presignRes.ok) throw new Error(`presign ${presignRes.status}`);
    const presign = (await presignRes.json()) as {
      success: boolean;
      uploadUrl: string;
      r2Key: string;
      publicUrl: string;
    };
    if (!presign.success) throw new Error('presign geweigerd');

    // 2. PUT rechtstreeks naar R2
    const putRes = await fetch(presign.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': item.mimeType },
      signal: metTimeout(item.type === 'video' ? PUT_TIMEOUT_VIDEO_MS : PUT_TIMEOUT_FOTO_MS),
      body,
    });
    if (!putRes.ok) throw new Error(`R2 PUT ${putRes.status}`);

    // 3. Registreer (idempotent op r2_key)
    const registerRes = await fetch('/api/media', {
      method: 'POST',
      headers,
      signal: metTimeout(API_TIMEOUT_MS),
      body: JSON.stringify({
        r2Key: presign.r2Key,
        url: presign.publicUrl,
        mimeType: item.mimeType,
        bytes: body.size,
        doel,
        fase: item.fase,
        captureStatus: item.captureStatus,
        takenAt: item.takenAt.toISOString(),
      }),
    });
    if (!registerRes.ok) throw new Error(`registratie ${registerRes.status}`);

    // 4. Klaar — blob van het toestel
    await db.mediaQueue.delete(item.id!);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Onbekende fout';
    console.warn('[mediaQueue] upload faalde:', message);
    laatsteFout = message;
    // 'failed' is een label voor de teller, geen eindstation: de volgende
    // ronde biedt het item opnieuw aan zodra de backoff verlopen is.
    // In eigen try: een update herserialiseert de blob en kan daarop zélf
    // falen ("Error preparing Blob/File data") — dat mag de worker-lus niet
    // omtrekken.
    try {
      await db.mediaQueue.update(item.id!, {
        status: item.retryCount + 1 >= MAX_RETRIES ? 'failed' : 'pending',
        retryCount: item.retryCount + 1,
        lastAttemptAt: new Date(),
        lastError: message,
      });
    } catch {
      // rij is onmuteerbaar; de leesbaarheids-check vangt hem volgende ronde
    }
  }
}

// Online-event: queue direct leegtrekken zodra de verbinding terug is.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void processMediaQueue();
  });
}
